import * as yup from 'yup';
import { typeModalMapping } from '../../../../components/ExpenseClaims/AdditionalDetailsModal/modalConfig';
import { DateUtil, TableUtil } from '../../../../utils';
import claimNoIcon from '../../../../assets/icons/claimnoicon.png';
import advPaidIcon from '../../../../assets/icons/advpaidicon.png';
import {
  iconsExpenseClaimDate,
  iconsExpenseClaimStatus,
  userIcon,
  departmentIcon,
} from '../../../../assets/index';

// helper to turn blank strings into null
function emptyStringToNull(value, originalValue) {
  return originalValue === '' ? null : value;
}

const baseExpenseClaimShape = {
  expCategory: yup.string().required('Expense Category is required'),
  expType: yup.string().required('Expense Type is required'),
  expDate: yup.string().required('Expense Date is required'),
  currency: yup.string().required('Currency is required'),
  amount: yup
    .number()
    .typeError('Expense Amount is required')
    .required('Expense Amount is required'),
  expRate: yup
    .number()
    .typeError('Exchange Rate is required')
    .required('Exchange Rate is required'),
  taxAmount: yup
    .number()
    .nullable()
    .transform(emptyStringToNull)
    .typeError('Tax Amount must be a number'),
  exptaxRecover: yup.string().required('Tax Recover is required'),
  reimburAmt: yup
    .number()
    .typeError('Reimbursement Amount must be a number')
    .required('Reimbursement Amount is required'),
  remarks: yup.string().notRequired(),
  acctCode: yup.string().required('Account Code is required'),
  baseRate: yup.number().required('Base Rate is required'),
  baseAmt: yup
    .number()
    .typeError('Base Amount must be a number')
    .required('Base Amount is required'),
};

const modalFieldsShape = {
  entity: yup.string(),
  costCentre: yup.string(),
  product: yup.string(),
  sorNo: yup.string(),
  costType: yup.string(),
  sMan: yup.string(),
  orderNo: yup.string(),
  regionCode: yup.string(),
  countryCode: yup.string(),
  reference: yup.string(),
  expCostAlloc: yup.string(),
  financeRemarks: yup.string(),
  dependent: yup.number(),
  expFromDate: yup.string(),
  expToDate: yup.string(),
};

// Build conditional modal fields schema based only on expType
const conditionalShape = Object.entries(modalFieldsShape).reduce(
  (acc, [field, schemaType]) => {
    acc[field] = schemaType.when('expType', (expType, schema) => {
      const config = typeModalMapping[expType] || {};
      const fieldConfig = config[field];

      // If there's a mapping for this field under the selected expType
      if (fieldConfig) {
        let s = schema;

        // Required handling
        if (fieldConfig.validation?.required?.value) {
          s = s.required(fieldConfig.validation.required.message);
          if (fieldConfig.datatype === 'number') {
            // Ensure that number is not -1
            s = s.test(
              'not-default',
              fieldConfig.validation.required.message,
              value => value !== -1,
            );
          }
        }

        // Regex pattern handling (if provided)
        if (fieldConfig.validation?.pattern) {
          s = s.matches(
            fieldConfig.validation.pattern.value,
            fieldConfig.validation.pattern.message,
          );
        }

        return s;
      }

      // Otherwise, leave as-is
      return schema;
    });

    return acc;
  },
  {},
);

// 6) Final row schema: merge base + all modal fields
export const rowSchema = yup.object().shape({
  ...baseExpenseClaimShape,
  ...conditionalShape,
});

export const validationSchema = yup.object({
  claims: yup
    .array()
    .transform((_, originalValue) => {
      if (!Array.isArray(originalValue)) {
        return originalValue;
      }
      return originalValue.length > 1
        ? originalValue.slice(0, -1)
        : originalValue;
    })
    .of(rowSchema)
    .min(1, 'At least one row is required'),
});

export const modalFieldInitialValues = {
  entity: '',
  costCentre: '',
  product: '',
  sorNo: '',
  costType: '',
  sMan: '',
  orderNo: '',
  regionCode: '',
  countryCode: '',
  reference: '',
  expCostAlloc: 'C00000',
  financeRemarks: '',
  dependent: -1,
  expFromDate: '',
  expToDate: '',
};

export const initialValues = {
  expCategory: '',
  expType: '',
  expDate: DateUtil.formatDateObjToHtmlDate(new Date()),
  currency: '',
  amount: '',
  expRate: '',
  taxAmount: '',
  exptaxRecover: 'N',
  reimburAmt: '',
  remarks: '',
  acctCode: '',
  baseRate: '',
  baseAmt: '',
  ...modalFieldInitialValues,
};

export const defaultValues = {
  claims: [initialValues],
};

export const processClaimSortConfig = [
  { key: 'expCategory', dataType: 'string', label: 'Category' },
  { key: 'expType', dataType: 'string', label: 'Type' },
  {
    key: 'expDate',
    dataType: 'date',
    label: 'Bill Date',
    sort: TableUtil.sortByDate,
  },
  { key: 'currency', dataType: 'string', label: 'Curr.' },
  { key: 'amount', dataType: 'number', label: 'Amount' },
  { key: 'taxAmount', dataType: 'number', label: 'VAT' },
  { key: 'exptaxRecover', dataType: 'string', label: 'Rec' },
  { key: 'expRate', dataType: 'number', label: 'Ex.Rate' },
  { key: 'reimburAmt', dataType: 'number', label: 'Pay Amt' },
  { key: 'remarks', dataType: 'string', label: 'Remarks' },
  { key: 'acctCode', dataType: 'string', label: 'AcCd' },
  { key: 'baseRate', dataType: 'number', label: 'Rate' },
  { key: 'baseAmt', dataType: 'number', label: 'Base Amt' },
];

export const processClaimModalSortConfig = [
  { key: 'entity', dataType: 'string', label: 'Entity' },
  { key: 'costCentre', dataType: 'string', label: 'Cost Centre' },
  { key: 'product', dataType: 'string', label: 'Product' },
  { key: 'sorNo', dataType: 'string', label: 'SOR No' },
  { key: 'costType', dataType: 'string', label: 'Cost Type' },
  { key: 'sMan', dataType: 'string', label: 'SMan' },
  { key: 'orderNo', dataType: 'string', label: 'Order No' },
  { key: 'regionCode', dataType: 'string', label: 'Region' },
  { key: 'countryCode', dataType: 'string', label: 'Country' },
  { key: 'reference', dataType: 'string', label: 'Reference' },
  { key: 'expCostAlloc', dataType: 'string', label: 'Cost Alloc' },
  { key: 'financeRemarks', dataType: 'string', label: 'Finance Remarks' },
  { key: 'dependent', dataType: 'number', label: 'Dependent' },
  { key: 'expFromDate', dataType: 'date', label: 'From Date' },
  { key: 'expToDate', dataType: 'date', label: 'To Date' },
];

export const firstHeaderColumn = headerData => [
  {
    title: 'Claim Number',
    value: headerData?.claimNo || '--',
    icon: claimNoIcon,
  },
  {
    title: 'Employee',
    value: headerData?.employee || '--',
    icon: userIcon,
  },
];

export const secondHeaderColumn = headerData => [
  {
    title: 'Claim Date',
    value: headerData?.claimDate
      ? DateUtil.isoToDdMmYyyySlice(headerData?.claimDate)
      : '--',
    icon: iconsExpenseClaimDate,
  },
  {
    title: 'Department',
    value: headerData?.department || '--',
    icon: departmentIcon,
  },
];

export const thirdHeaderColumn = headerData => [
  {
    title: 'Cost Center',
    icon: advPaidIcon,
    value: headerData?.costCenter || '--',
  },
  {
    title: 'Claim Status',
    icon: iconsExpenseClaimStatus,
    value: headerData?.claimStatus || '--',
  },
];
